import { DEFAULT_THRESHOLDS, type Thresholds } from './thresholds';

export type ServiceStartProvenance = 'in_district' | 'transferred_in' | 'missing';

export type TenureInput = {
  serviceStartYear?: number | null;
  yearsInEl?: number | null;
  startedInDistrict?: boolean | null;
  newcomerFlag?: boolean | null;
  earliestWidaYear?: number | null;
  currentYear: number;
  thresholds?: Thresholds;
};

export type StudentTenure = {
  tenure: {
    yearsInProgram: number | null;
    serviceStartProvenance: ServiceStartProvenance;
    observedYearsFloor: number | null;
    tenureIsCertain: boolean;
  };
  newcomer: {
    isNewcomer: boolean;
    newcomerMaxYearsUsed: number;
    needsDistrictConfirmation: boolean;
  };
  ltel: {
    isLtel: boolean;
    yearsInProgram: number | null;
    ltelMinYearsUsed: number;
    needsDistrictConfirmation: boolean;
  };
};

export function computeClassificationTenure(input: TenureInput): StudentTenure {
  const t = input.thresholds ?? DEFAULT_THRESHOLDS;

  const yearsInProgram =
    input.serviceStartYear != null
      ? input.currentYear - input.serviceStartYear + 1
      : (input.yearsInEl ?? null);

  const provenance: ServiceStartProvenance =
    yearsInProgram == null
      ? 'missing'
      : input.startedInDistrict === true
        ? 'in_district'
        : 'transferred_in';

  const observedYearsFloor =
    input.earliestWidaYear != null ? input.currentYear - input.earliestWidaYear + 1 : null;

  const ltelConfirmed =
    (yearsInProgram != null && yearsInProgram >= t.ltelMinYears) ||
    (observedYearsFloor != null && observedYearsFloor >= t.ltelMinYears);
  const ltelUncertain = !ltelConfirmed && provenance !== 'in_district';

  let isNewcomer = false;
  let newcomerNeedsConfirmation = false;
  if (provenance === 'in_district' && yearsInProgram != null && yearsInProgram <= t.newcomerMaxYears) {
    isNewcomer = true;
  } else if (input.newcomerFlag != null) {
    isNewcomer = input.newcomerFlag;
  } else if (provenance !== 'in_district') {
    newcomerNeedsConfirmation = true;
  }

  return {
    tenure: {
      yearsInProgram,
      serviceStartProvenance: provenance,
      observedYearsFloor,
      tenureIsCertain: provenance === 'in_district',
    },
    newcomer: {
      isNewcomer,
      newcomerMaxYearsUsed: t.newcomerMaxYears,
      needsDistrictConfirmation: newcomerNeedsConfirmation,
    },
    ltel: {
      isLtel: ltelConfirmed,
      yearsInProgram,
      ltelMinYearsUsed: t.ltelMinYears,
      needsDistrictConfirmation: ltelUncertain,
    },
  };
}

export function resolveStartedInDistrict(
  raw: string | null | undefined,
  currentDistrict: string | null | undefined,
): boolean | null {
  const value = raw?.trim() ?? '';
  if (value === '') return null;
  switch (value.toLowerCase()) {
    case 'y':
    case 'yes':
    case 'true':
    case 't':
    case '1':
      return true;
    case 'n':
    case 'no':
    case 'false':
    case 'f':
    case '0':
      return false;
  }
  const current = currentDistrict?.trim() ?? '';
  if (current === '') return null;
  return value.toLowerCase() === current.toLowerCase();
}
